import { ConfigProvider, Layout } from 'antd';
import en_US from 'antd/es/locale/en_US';
import ru_RU from 'antd/es/locale/ru_RU';
import es_ES from 'antd/es/locale/es_ES';
import api from 'api';
import SessionComponent from 'components/SessionComponent/SessionComponent';
import WorkerComponent from 'components/SessionComponent/WorkerComponent';
import SideBar from 'components/SideBar/SideBar';
import systemPages, { getKeyFromLocation, technicalPages } from 'helpers/pagesAndLocation';
import { checkIsTariffEnded, checkIsAwaitingPayment } from 'helpers/tariffPayment';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { fetchChatId } from 'store/reducers/chatCreator';
import { fetchRoom } from 'store/reducers/roomsCreator';
import { addExternalAuth, fetchUserData, fetchUserPermissions, fetchUserRooms } from 'store/reducers/userDataCreator';
import { setCurrentFolder } from 'store/slices/dataDocumentsSlice';
import { setFoldersForPermissions, setUserPermissions } from 'store/slices/dataPermissionsSlice';
import { fetchGlobalUsers } from 'store/reducers/usersGroupCreator';
import { setRooms, setUserAndRoomStatus, setUserData } from 'store/slices/userDataSlice';
import { setIsOpenChangePassword, setIsOpenFailedPayment, setIsOpenSuccessfulPayment, setIsRSidebarCollapsed, setLogoutInProcess, setSelectedKeys } from 'store/slices/windowStateSlice';
import Spinner from 'components/Spinner/Spinner';
import classes from './Main.module.scss';
import cn from 'classnames';
import { setCheckingMessages } from 'store/slices/chatsSlice';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import ru from'dayjs/locale/ru';
import es from'dayjs/locale/es';
import en from'dayjs/locale/en';
import duration from 'dayjs/plugin/duration';
import isToday from 'dayjs/plugin/isToday';
import SuccessfulPayment from 'components/Modals/SuccessfulPayment/SuccessfulPayment';
import SideBarMenu from 'components/SideBarMenu/SideBarMenu';
import RightSideBar from 'components/RightSideBar/RightSideBar';
import Navigation from 'components/Header/Navigation';
import { ConfigureRoles, InviteUsersModal, NewFolderModal, TariffInfoModal } from 'components/Modals';
import { checkRefreshToken } from 'helpers/cleanLocalStorage';
import FailedPayment from 'components/Modals/FailedPayment/FailedPayment';
import { setIsOpenConfrimInvite, setRoomToConfirm } from 'store/slices/dataRoomSlice';
import ChangePassword from 'components/Modals/ChangePassword/ChangePassword';
import { UploadProgressPanel } from 'components/UploadProgressPanel/UploadProgressPanel';
import ConfigureFolder from 'components/Modals/ConfigureFolder/ConfigureFolder';
import flags from 'assets/flags';
import GenerateFileModal from 'components/Modals/GenerateFileModal/GenerateFileModal';
import CreateEmptyFileModal from 'components/Modals/CreateEmptyFileModal/CreateEmptyFileModal';
import AllowNewMessageSound from 'components/Notifications/AllowNewMessageSound/AllowNewMessageSound';
import { useNotificationSound } from 'hooks/useNotificationSound/useNotificationSound';
import auth from 'api/auth';
import TourElement from 'components/Header/TourElement';

const isBillingActive = window.REACT_APP_IS_BILLING_ACTIVE !== 'REPLACE_REACT_APP_IS_BILLING_ACTIVE' ? window.REACT_APP_IS_BILLING_ACTIVE : process.env.REACT_APP_IS_BILLING_ACTIVE;

dayjs.extend(duration);
dayjs.extend(relativeTime);
dayjs.extend(isToday);

export const languagesValues = [
  { value: 'en',  label: <span className={classes.lng}><img src={flags.gb} alt='gb'/> <Trans i18nKey='languages.en' /> </span> },
  { value: 'ru', label: <span className={classes.lng}><img src={flags.ru} alt='ru'/> <Trans i18nKey='languages.ru' /> </span>},
  { value: 'kg', label: <span className={classes.lng}><img src={flags.kg} alt='kg'/> <Trans i18nKey='languages.kg' /> </span>},
  { value: 'es', label: <span className={classes.lng}><img src={flags.es} alt='es'/> <Trans i18nKey='languages.es' /> </span>},
];
export const languageConfigs = { ru: ru_RU, en: en_US, kg: en_US, es: es_ES };
const dayjsLocales = { ru, en, kg: en, es };

export default function Main() {  
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const currentLanguageConfig = languageConfigs[i18n.resolvedLanguage as keyof typeof languageConfigs];
  const isMobile = /Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(navigator.userAgent);
  dayjs.locale(dayjsLocales[i18n.resolvedLanguage as keyof typeof dayjsLocales] || en)
  
  const { userData, userAndRoomStatus, available_rooms } = useAppSelector((store) => store.userData);
  const { currentFolder } = useAppSelector((store) => store.documents);  
  const { dataRoom, isFolderCreateOpen, isFileCreateOpen, isEmptyFileCreateOpen } = useAppSelector((store) => store.dataRoom);
  const { isConfigureFolderOpen, userFoldersPermissions, userRootPermissions, foldersForPermissions } = useAppSelector((store) => store.permissions);
  const { isConfigureRolesOpen } = useAppSelector((store) => store.dataUsers);  
  const { isResizing, isOpenSuccessfulPayment, isLoadingProcess, isInviteUserOpen, isTourOpen,
    triggerNdaLeave, isOpenFailedPayment, isOpenChangePassword, messageApi, soundNotificationState
  } = useAppSelector((store) => store.windowState);
  
  const parentFolderID = currentFolder[0] ? currentFolder[0]?.id : null;
  const currentFolderPermissions = parentFolderID 
    ? userFoldersPermissions?.find(folder => folder.id === parentFolderID) || foldersForPermissions?.find(folder => folder.id === parentFolderID)
    : userRootPermissions;
  const isAvailableUploading: boolean = currentFolderPermissions?.permissions ? currentFolderPermissions?.permissions?.includes('can_upload') : false;
  
  const [currentKey] =  useAppSelector((store) => store.windowState.selectedKeys);  

  const { enableSound } = useNotificationSound();
  const { pathname: location } = useLocation();
  const params = useParams();
  const selectedKeys = getKeyFromLocation(location, params.room);
  const isNeedToDisplayRoomNavigation = !selectedKeys.includes('mobile') && ![...systemPages, ...technicalPages].includes(selectedKeys);
  const cases = {
    pendingUserData: async () => {      
      dispatch(setSelectedKeys([selectedKeys]));
      await dispatch(fetchUserData({navigate, t}));
      await dispatch(fetchUserRooms({}));
    },

    emptyDataRoom: () => {      
      dispatch(setSelectedKeys(['rooms']));
      localStorage.removeItem('previousRoomId');
      localStorage.removeItem('previousFolderId');
      navigate('/rooms');
    },

    fulfilledUserData: async () => {
      const previousPage = localStorage.getItem('previousPage') || 'rooms';
      const chatId = params?.chatId || localStorage.getItem('previousChatId');
      const lastRoomId = params.room || localStorage.getItem('previousRoomId');
      const targetRoom = available_rooms?.find(room => room.id === lastRoomId)
      const isAvailableLastRoom = targetRoom ? !Boolean(targetRoom?.invite_id) : false;
      
      if (!lastRoomId && systemPages.includes(previousPage)) {
        const rightPath = previousPage === 'global-chats' && chatId ? `${previousPage}/${chatId}` : previousPage;
        previousPage ? navigate(rightPath) : navigate('/rooms');
        dispatch(setSelectedKeys([previousPage]));
      } else if (lastRoomId && !systemPages.includes(previousPage) && isAvailableLastRoom) {        
        await dispatch(fetchUserPermissions({ idRoom: lastRoomId }));
        await dispatch(fetchRoom({ id: lastRoomId, navigate }));
        dispatch(setUserAndRoomStatus('fulfilledDataRoom'));
      } else if (params.room && !targetRoom) {
        messageApi?.error(t('main.inaccessibleRoom'), 10);
        dispatch(setUserAndRoomStatus('emptyDataRoom'));
      } else {        
        if (targetRoom?.invite_id) {
          dispatch(setIsOpenConfrimInvite(true));
          dispatch(setRoomToConfirm(targetRoom));
          navigate('/rooms');
        } else {
          navigate('/rooms');
        };
        localStorage.removeItem('previousRoomId');
      }
      dispatch(fetchGlobalUsers());
      // dispatch(fetchGlobalChats({ ownerId: userData?.id!, chatId: chatId || null }));
    },

    fulfilledDataRoom: async () => {      
      const checkingMessages = JSON.parse(localStorage.getItem(userData?.email!) || '[]');
      dispatch(setCheckingMessages(checkingMessages));

      if (dataRoom?.is_blocked) {
        const rootFolder = {
          id: 0,
          name: 'Root',
          permissions: [],
          can_edit_permissions: true,
          is_agreed: null,
          folder_path: null
        };
        dispatch(setUserPermissions({ documents: [], folders: [], rootFolder }));
        navigate(`/room/${dataRoom?.id}/documents`);        
        return;
      }
      // RETURNED BILLING
      if (isBillingActive === 'true') {
        if (checkIsTariffEnded(dataRoom)) {
          navigate(`/room/${dataRoom?.id}/endedTariff`);
          return;
        } else if (checkIsAwaitingPayment(dataRoom)) {
       console.log('awaitingPayment')
          navigate(`/room/${dataRoom?.id}/awaitingPayment`);
          return;
        }
      }
       
      const folderIdFromMemory = params.folderId || localStorage.getItem('previousFolderId');
      // запись пути папки (из URL или previousFolderId) в LocalStorage как 'previousFolderPath';
      if (folderIdFromMemory && dataRoom) await getFolderPath(folderIdFromMemory, dataRoom?.id);
      
      const previousFolderPath: any[] = JSON.parse(localStorage.getItem('previousFolderPath') || '[]');
      const previousPage = localStorage.getItem('previousPage') || 'rooms';
      const chatId = params?.chatId || localStorage.getItem('previousChatId');
      
      dispatch(setCurrentFolder(previousFolderPath))
      dispatch(fetchChatId({ roomId: dataRoom?.id!, ownerId: userData?.id!, chatId: chatId ||  null }));

      const lastFolderId = previousFolderPath[previousFolderPath.length - 1]?.id;

      const entityId = previousPage === 'documents' && lastFolderId
        ? `${lastFolderId}`
        : (chatId && previousPage.includes('chat'))
          ? `${chatId}`
          : '';      
      
      const entityPath = entityId ? `/${entityId}` : '';
      const roomId = params.room || dataRoom?.custom_url || dataRoom?.id;
      const locationWithId = !roomId
        ? '/rooms'
        : `/room/${roomId}/${previousPage}${entityPath}`;

      let navigatePath = systemPages.includes(previousPage)
        ? previousPage
        : locationWithId;

      if (isMobile) navigate('/unsupported');
        
      const isNdaRequired = dataRoom?.user_status === 1 || dataRoom?.access_terms_status == 'waiting_for_approve';
      const targetPath = isNdaRequired ? `/room/${roomId}/nda` : navigatePath;
      const currentPath = window.location.pathname.replace(/\/+$/, '');
      const normalizedTargetPath = targetPath.replace(/\/+$/, '');

      if (currentPath !== normalizedTargetPath) {
        const query = window.location.search;
        navigate(query ? `${targetPath}${query}` : targetPath, { replace: true });
      }
      
      localStorage.removeItem('previousChatId');
      getMyPermissions();
    },
  };

  const checkInvites = async () => {
    const infvite_info = localStorage.getItem('invite_hash');
    if (infvite_info && userData?.email) {
      let invite: any;
      try {
        invite = await auth.inviteSignup(infvite_info);
      } catch (e) {
        messageApi?.error(t('login.inviteNotAvailable'), 8);
        localStorage.removeItem('invite_hash');
      }
      if (invite?.data?.is_parent_invite && userData?.email) {
        try {
          await api.acceptParentInvite({ parent_invite_id: invite.data?.invite_id, email: userData?.email });
          setTimeout(() => { messageApi?.success(t('login.newInvite'), 8) }, 2000);
        } catch {
          messageApi?.success(t('login.alreadyUsed'), 8);
        }
        localStorage.removeItem('invite_hash');
        sessionStorage.removeItem('pending_parent_invite');
      }
    }    
  };

  React.useEffect(() => {
    const checkRefreshTokenFunction = () => checkRefreshToken(navigate, t, messageApi!);
    dispatch(setLogoutInProcess(false));
    window.addEventListener('click', checkRefreshTokenFunction);
    const unlockAudio = () => {
      enableSound();
      window.removeEventListener('click', unlockAudio);
    };
    if (localStorage.getItem('alreadyAllowSound') === 'true' && !soundNotificationState.enabled) {
      // Проверки показали что это метод ненадёжный, в частности в safari этого недостачтоно чтобы дать доступ к последующему воспроизведению
      // const allowSoundBtn = document?.querySelector('#allowSoundBtn') as HTMLElement;
      // allowSoundBtn?.click();
      window.addEventListener('click', unlockAudio);
    };
    return () => {
      dispatch(setUserData(null));
      dispatch(setRooms([]));
      dispatch(setUserAndRoomStatus('pendingUserData'));
      window.removeEventListener('click', checkRefreshTokenFunction);
      window.removeEventListener('click', unlockAudio);
    };
  }, []);

  React.useEffect(() => {
    checkInvites();
  }, [userData?.email]);

  React.useEffect(() => {
    try {
      (cases[userAndRoomStatus as keyof typeof cases] || function () {})();
    } catch(err) {
      messageApi?.error(t('Header.error.personalData'));
    }
  }, [userAndRoomStatus]);

  React.useEffect(() => {
    if (systemPages.includes(currentKey)) dispatch(setIsRSidebarCollapsed({collapsed: true, entity: '', width: 0}));
    if (dataRoom?.access_terms_status === 'waiting_for_approve') dispatch(setIsRSidebarCollapsed({collapsed: true, entity: '', width: 0}));
  }, [currentKey, dataRoom]);

  React.useEffect(() => {
    if(triggerNdaLeave && dataRoom && dataRoom.agreement_enable && dataRoom?.user_status !== 2) {
      messageApi?.warning(t('Settings.terms.nonAcceptWarning'));
    }
  },[triggerNdaLeave])

  React.useEffect(() => {
    if (window.location.search) {
      const urlParams = new URLSearchParams(window.location.search);
      const paramsObject = Object.fromEntries(urlParams.entries());
      if (paramsObject?.code && paramsObject?.state) {
        dispatch(addExternalAuth({
          params: { code: paramsObject?.code, device_id: paramsObject?.device_id, state: paramsObject?.state, cid: paramsObject?.cid },
          auth_name: paramsObject?.device_id ? 'vk' : 'yandex',
          t,
        }));
      };
    };
  }, [window.location.search]);

  const getMyPermissions = async () => {
    const { data: foldersPermissionsReq } = await api.getFoldersPermissions({ room_id: dataRoom?.id! });
    const rootFolder = foldersPermissionsReq.find((el: any) => el.id === 0);
    const onlyFolders = foldersPermissionsReq.filter((el: any) => el.id !== 0);   
    const foldersWithPermissions = (onlyFolders[0] ? onlyFolders : dataRoom?.folders) || [];
    const foldersWithoutNull = foldersWithPermissions?.map(folder => ({
      ...folder,
      permissions: folder.permissions && folder?.permissions[0] ? folder.permissions : []
    }));

    dispatch(setUserPermissions({ documents: [], folders: foldersWithoutNull, rootFolder }));
    dispatch(setUserAndRoomStatus(''));
    dispatch(
      setFoldersForPermissions(foldersWithoutNull.map((folder) => ({ ...folder, key: `folder ${folder.id}`, type: 'folder' })))
    );
  };
  
  const getFolderPath = async (folderId: string, roomId: string) => {
    try {
      const folder = await api.getFolderById(folderId, roomId)
      const folderPath = folder.data.folder_path ? folder.data.folder_path?.reverse() : [];
      localStorage.setItem('previousFolderPath', JSON.stringify(folderPath));
    } catch (e) {
      messageApi?.error(t('main.inaccessibleFolder'), 10)
      localStorage.setItem('previousFolderPath', JSON.stringify([]));
    }
    localStorage.removeItem('previousFolderId');
  };

  const closeChangePasswordModal = () => {
    dispatch(setIsOpenChangePassword(false))
    localStorage.removeItem('link_id_rp');
    localStorage.removeItem('userEmail_rp');
    localStorage.removeItem('user_id_rp');
  }

  if (isMobile && !userData) {
    return (
      <div className={classes.spinner}>
        <Spinner size={100} />
      </div>
    );
  };

  if (isMobile && userData) {
    return <SideBar />
  };  

  return (
    <Layout className={cn(classes.mainDiv, isResizing ? classes.resizing : 'null')}>
      <ConfigProvider locale={currentLanguageConfig}>

        <SideBarMenu />

        <SideBar/>

        <Layout className={classes.whiteLayout}>
          <div className={cn(classes.outletWrap, isLoadingProcess ? classes.loading : 'null')}>
            {isNeedToDisplayRoomNavigation && (
              <Navigation isAvailableUploading={isAvailableUploading}/>
            )}
            <Outlet />
          </div>
          {window.SharedWorker ? <WorkerComponent /> : <SessionComponent />}

        </Layout>

        <UploadProgressPanel />
        <RightSideBar />

        {isTourOpen && <TourElement isAvailableUploading={isAvailableUploading}/>}

        <AllowNewMessageSound />

        {isOpenSuccessfulPayment && userData && (
          <React.Suspense fallback={<div />}>
            <SuccessfulPayment
              isOpen={isOpenSuccessfulPayment}
              onCancel={() => dispatch(setIsOpenSuccessfulPayment(false))}
            />
          </React.Suspense>
        )}

        {isOpenChangePassword && userData && (
          <React.Suspense fallback={<div />}>
            <ChangePassword
              isOpen={isOpenChangePassword}
              onCancel={closeChangePasswordModal}
            />
          </React.Suspense>
        )}
        
        {isOpenFailedPayment && userData && (
          <React.Suspense fallback={<div />}>
            <FailedPayment
              isOpen={isOpenFailedPayment}
              onCancel={() => dispatch(setIsOpenFailedPayment(false))}
            />
          </React.Suspense>
        )}

        {isInviteUserOpen && (
          <React.Suspense fallback={<div />}>
            <InviteUsersModal isOpen={isInviteUserOpen}/>
          </React.Suspense>
        )}

        {isFolderCreateOpen && (
          <React.Suspense fallback={<div />}>
            <NewFolderModal
              roomId={dataRoom?.id!} 
              open={isFolderCreateOpen}
            />
          </React.Suspense>
        )}

        {isFileCreateOpen && (
          <React.Suspense fallback={<div />}>
            <GenerateFileModal
              open={isFileCreateOpen}
            />
          </React.Suspense>
        )}

        {isEmptyFileCreateOpen && (
          <React.Suspense fallback={<div />}>
            <CreateEmptyFileModal
              open={isEmptyFileCreateOpen}
            />
          </React.Suspense>
        )}

        {isConfigureRolesOpen && (
          <React.Suspense fallback={<div />}>
            <ConfigureRoles
              isOpen={isConfigureRolesOpen}
            />
          </React.Suspense>
        )}

        {isConfigureFolderOpen && (
          <React.Suspense fallback={<div />}>
            <ConfigureFolder
              isOpen={isConfigureFolderOpen}
            />
          </React.Suspense>
        )}

        <TariffInfoModal />

      </ConfigProvider>
    </Layout>
  );
}
